import apiFetch from '@wordpress/api-fetch';

declare var __ALLCOACH_ACTIVITY__: {
	baseEndpoint: string;
	enrollId: number;
	totalActs: number;
	completedN: number;
	canSubmit: boolean;
	i18n: {
		completed: string;
		submitting: string;
		submit: string;
		uploadFailed: string;
		somethingWentWrong: string;
		of: string;
		completedLabel: string;
		required: string;
		invalidFileType: string;
	};
};

export function initActivities(): void {
	if (typeof __ALLCOACH_ACTIVITY__ === 'undefined') return;

	const { baseEndpoint, enrollId, totalActs, i18n } = __ALLCOACH_ACTIVITY__;
	let { completedN } = __ALLCOACH_ACTIVITY__;

	const pill = document.getElementById('ac-act-status-pill');
	const menu = document.getElementById('ac-status-menu');
	const form = document.getElementById('ac-act-form') as HTMLFormElement | null;

	if (pill && menu) {
		pill.addEventListener('click', (e) => {
			e.stopPropagation();
			const open = menu.classList.toggle('is-open');
			pill.setAttribute('aria-expanded', open ? 'true' : 'false');
		});

		document.addEventListener('click', () => {
			menu.classList.remove('is-open');
			pill.setAttribute('aria-expanded', 'false');
		});

		menu
			.querySelectorAll<HTMLButtonElement>('.ac-status-menu__item')
			.forEach((btn) => {
				btn.addEventListener('click', (e) => {
					e.stopPropagation();
					menu.classList.remove('is-open');
					if (btn.dataset.status === 'completed') submitCompletion();
				});
			});
	}

	form?.addEventListener('submit', (e) => {
		e.preventDefault();
		submitCompletion();
	});

	function clearErrors(): void {
		document
			.querySelectorAll('.ac-act-field-error')
			.forEach((el) => el.remove());
		document
			.querySelectorAll(
				'.ac-act-field.is-invalid, .ac-act-file-input.is-invalid',
			)
			.forEach((el) => {
				el.classList.remove('is-invalid');
			});
	}

	function showFieldError(el: HTMLElement, message: string): void {
		const block = el.closest('.ac-act-block');
		if (!block) return;
		el.classList.add('is-invalid');
		const span = document.createElement('span');
		span.className = 'ac-act-field-error';
		span.textContent = message;
		block.appendChild(span);
	}

	function isFileTypeValid(file: File, accept: string): boolean {
		return accept
			.split(',')
			.map((s) => s.trim().toLowerCase())
			.some((pattern) => {
				if (pattern.startsWith('.'))
					return file.name.toLowerCase().endsWith(pattern);
				if (pattern.endsWith('/*'))
					return file.type.toLowerCase().startsWith(pattern.slice(0, -1));
				return file.type.toLowerCase() === pattern;
			});
	}

	function validateForm(): boolean {
		clearErrors();
		let valid = true;

		document
			.querySelectorAll<HTMLInputElement>('.ac-act-field[required]')
			.forEach((el) => {
				if (!el.value.trim()) {
					showFieldError(el, i18n.required);
					valid = false;
				}
			});

		document
			.querySelectorAll<HTMLTextAreaElement>('textarea.ac-act-field[required]')
			.forEach((el) => {
				if (!el.value.trim()) {
					showFieldError(el, i18n.required);
					valid = false;
				}
			});

		const radioGroups = new Set<string>();
		document
			.querySelectorAll<HTMLInputElement>(
				'input[type="radio"][required][data-field]',
			)
			.forEach((el) => {
				radioGroups.add(el.dataset.field!);
			});
		radioGroups.forEach((fieldId) => {
			const checked = document.querySelector<HTMLInputElement>(
				`input[type="radio"][data-field="${CSS.escape(fieldId)}"]:checked`,
			);
			if (!checked) {
				const first = document.querySelector<HTMLInputElement>(
					`input[type="radio"][data-field="${CSS.escape(fieldId)}"]`,
				);
				if (first) {
					showFieldError(first, i18n.required);
					valid = false;
				}
			}
		});

		const checkboxGroups = new Set<string>();
		document
			.querySelectorAll<HTMLInputElement>(
				'input[type="checkbox"][required][data-field]',
			)
			.forEach((el) => {
				checkboxGroups.add(el.dataset.field!);
			});
		checkboxGroups.forEach((fieldId) => {
			const checked = document.querySelector<HTMLInputElement>(
				`input[type="checkbox"][data-field="${CSS.escape(fieldId)}"]:checked`,
			);
			if (!checked) {
				const first = document.querySelector<HTMLInputElement>(
					`input[type="checkbox"][data-field="${CSS.escape(fieldId)}"]`,
				);
				if (first) {
					showFieldError(first, i18n.required);
					valid = false;
				}
			}
		});

		document
			.querySelectorAll<HTMLInputElement>('input[type="file"][data-field]')
			.forEach((el) => {
				if (el.required && !el.files?.[0]) {
					showFieldError(el, i18n.required);
					valid = false;
					return;
				}
				if (
					el.files?.[0] &&
					el.accept &&
					!isFileTypeValid(el.files[0], el.accept)
				) {
					showFieldError(el, i18n.invalidFileType);
					valid = false;
				}
			});

		return valid;
	}

	function collectResponseData(): Record<string, unknown> {
		const result: Record<string, unknown> = {};
		const checkboxes: Record<string, string[]> = {};

		document.querySelectorAll<HTMLElement>('[data-field]').forEach((el) => {
			const key = el.dataset.field!;

			if (el instanceof HTMLInputElement) {
				if (el.type === 'radio') {
					if (el.checked) result[key] = el.value;
				} else if (el.type === 'checkbox') {
					checkboxes[key] ??= [];
					if (el.checked) checkboxes[key].push(el.value);
				} else if (el.type !== 'file') {
					result[key] = el.value;
				}
			} else if (el instanceof HTMLTextAreaElement) {
				result[key] = el.value;
			}
		});

		return { ...result, ...checkboxes };
	}

	function markCompletedUI(): void {
		if (pill) {
			pill.className = 'ac-pill ac-pill--completed';
			pill.innerHTML =
				i18n.completed +
				'<svg width="9" height="9" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" style="margin-inline-start:3px"><path d="M6 9l6 6 6-6"/></svg>';
		}

		const footer = document.getElementById('ac-act-footer');
		if (footer) footer.style.display = 'none';

		completedN += 1;
		const pct = totalActs > 0 ? Math.round((completedN / totalActs) * 100) : 0;
		const bar = document.getElementById(
			'ac-act-prog-bar',
		) as HTMLElement | null;
		const text = document.getElementById('ac-act-prog-text');
		if (bar) bar.style.width = `${pct}%`;
		if (text)
			text.textContent = `${completedN} ${i18n.of} ${totalActs} ${i18n.completedLabel}`;

		document
			.querySelectorAll<HTMLInputElement>(
				'.ac-act-field, .ac-act-option input, .ac-act-file-input',
			)
			.forEach((el) => {
				el.disabled = true;
			});
	}

	function uploadPendingFiles(): Promise<
		Array<{ fieldId: string; url: string; name: string }>
	> {
		const promises: Promise<{ fieldId: string; url: string; name: string }>[] =
			[];

		document
			.querySelectorAll<HTMLInputElement>('input[type="file"][data-field]')
			.forEach((input) => {
				if (!input.files?.[0]) return;

				const formData = new FormData();
				formData.append('file', input.files[0]);
				formData.append('field_id', input.dataset.field!);

				promises.push(
					apiFetch({
						path: baseEndpoint + '/upload',
						method: 'POST',
						body: formData,
					}).then((res) => {
						const data = (res as { data: { url: string; name: string } }).data;
						return {
							fieldId: input.dataset.field!,
							url: data.url,
							name: data.name,
						};
					}),
				);
			});

		return Promise.all(promises);
	}

	function submitCompletion(): void {
		if (!enrollId) return;
		if (!validateForm()) return;

		const btn = document.getElementById(
			'ac-act-submit',
		) as HTMLButtonElement | null;
		const err = document.getElementById('ac-act-error');
		if (btn) {
			btn.disabled = true;
			btn.textContent = i18n.submitting;
		}
		if (err) err.style.display = 'none';

		uploadPendingFiles()
			.then((uploaded) => {
				const responseData = collectResponseData();
				uploaded.forEach((f) => {
					responseData[f.fieldId] = { url: f.url, name: f.name };
				});

				return apiFetch({
					path: baseEndpoint + '/complete',
					method: 'POST',
					data: { enrollment_id: enrollId, response_data: responseData },
				});
			})
			.then(() => {
				markCompletedUI();
			})
			.catch((e: Error) => {
				if (btn) {
					btn.disabled = false;
					btn.textContent = i18n.submit;
				}
				if (err) {
					err.textContent = e.message;
					err.style.display = 'inline';
				}
			});
	}
}
